import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { ApiService } from '@domgen/data-access-claims';
import { DgxComponentsButtonModule } from '@domgen/dgx-fe-components/button';
import { take } from 'rxjs';
import { SmartFixAnalyticsService } from '../../services/smart-fix-analytics/smart-fix-analytics.service';
import { Claim } from '@domgen/dgx-components';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { HttpClient } from '@angular/common/http';
import confetti from 'canvas-confetti';
import { SmartFixFeedbackModalComponent } from '../smart-fix-feedback-modal/smart-fix-feedback-modal';
export interface FeedbackOption {
  label: string;
  icon?: any;
  value: boolean | null;
  children?: FeedbackOption[];
  message?: string;
  callBack?: any;
  skipTracking?: boolean;
  requiresText?: boolean;
}

@Component({
  selector: 'smart-fix-feedback',
  templateUrl: './smart-fix-feedback.component.html',
  styleUrls: [
    './smart-fix-feedback.component.scss',
    '../../../../../feature-shell/src/lib/standalone-smart-fix/standalone-smart-fix.component.scss',
  ],
  imports: [CommonModule, DgxComponentsButtonModule, FormsModule, RouterModule],
  standalone: true,
})
export class SmartFixFeedbackComponent {
  @Input() sessionId: string | undefined;
  @Input() title: string | undefined;
  @Input() claim!: Claim;
  @Output() isSubmittingFeedback: EventEmitter<boolean> = new EventEmitter();

  feedbackText: string = '';
  showErrorMessage: boolean = false;
  rating: number = 0;
  ratingScale = [1, 2, 3, 4, 5];
  resolvedOptions = ['Yes, it did', `No, it didn't`];
  selectedOption: string | null = '';
  showPositiveMessage: boolean = false;

  starSvg: SafeHtml | null = null;

  constructor(
    private apiService: ApiService,
    private router: Router,
    private smartFixAnalytics: SmartFixAnalyticsService,
    private sanitizer: DomSanitizer,
    private http: HttpClient
  ) {}

  ngOnInit(): void {
    this.http
      .get('/assets/svg/smart-fix-star.svg', { responseType: 'text' })
      .subscribe((file) => {
        this.starSvg = this.sanitizer.bypassSecurityTrustHtml(file);
      });

    this.smartFixAnalytics.pageViewEvent(
      'claims-sf-feedback-score',
      this.claim
    );
  }

  selectOption(option: string) {
    this.selectedOption = option;
  }

  setRating(star: number): void {
    this.rating = star;
    this.showErrorMessage = false;

    if (this.rating === 5) {
      this.showPositiveMessage = true;
      this.triggerConfetti();
    } else {
      this.showPositiveMessage = false;
    }
  }

  triggerConfetti() {
    const duration = 3000;
    const animationEnd = Date.now() + duration;
    const defaults = {
      startVelocity: 30,
      spread: 360,
      ticks: 60,
      zIndex: 0,
    };

    const interval: ReturnType<typeof setInterval> = setInterval(() => {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 50 * (timeLeft / duration);

      confetti({
        ...defaults,
        particleCount,
        origin: { x: Math.random(), y: 0 },
        colors: [
          '#0065C4',
          '#9848B3',
          '#6160C0',
          '#1980D8',
          '#FFDC5D',
          '#5DADEC',
        ],
      });
    }, 250);
  }

  onFeedbackTextInput(): void {
    if (this.feedbackText) {
      this.showErrorMessage = false;
    }
  }

  submitFeedback(feedback: string): void {
    if (!this.rating && !this.feedbackText && this.selectedOption === '') {
      this.showErrorMessage = true;
      return;
    }

    if (this.sessionId) {
      this.trackFeedbackGA();
      this.isSubmittingFeedback.emit(true);

      this.apiService
        .addBedrockChatFeedback(
          this.selectedOption === this.resolvedOptions[0],
          feedback,
          this.rating.toString(),
          this.sessionId || ''
        )
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.isSubmittingFeedback.emit(false);
            // If resolved, redirect back to MMR
            if (this.selectedOption === this.resolvedOptions[0]) {
              this.router.navigate([
                '/',
                'my-plans',
                this.claim.reflect?.planNumber,
                'claims',
              ]);
              return;
            }
            this.navigateToBookingForm();
          },
          error: () => {
            this.isSubmittingFeedback.emit(false);
          },
        });
    }
  }

  goToBookEngineer(): void {
    const event = 'skip book an engineer';
    this.navigateToBookingForm();

    this.smartFixAnalytics.triggerSmartFixEvent(
      event,
      '',
      this.claim,
      'smart-fix-feedback',
      'claims-sf-feedback-score'
    );
  }

  navigateToBookingForm(): void {
    this.router.navigate(['repair-details'], {
      replaceUrl: true,
    });
  }

  trackFeedbackGA(): void {
    const eventCategory = 'smart-fix-feedback';

    const triggerEvent = (eventLabel: string, eventAction: string) => {
      this.smartFixAnalytics.triggerSmartFixEvent(
        eventLabel,
        '',
        this.claim,
        eventAction,
        eventCategory,
        'claims-sf-feedback-score'
      );
    };

    if (this.selectedOption) {
      triggerEvent(this.selectedOption, 'did fix your issue');
    }

    if (this.rating) {
      triggerEvent(`${this.rating} star`, 'how did you rate your experience');
    }

    if (this.feedbackText) {
      triggerEvent(this.feedbackText, 'how did you like or dislike');
    }
  }

  trackByStar(star: number, index: number): string {
    return star.toString();
  }

  get submitButtonLabel(): string {
    if (!this.selectedOption) {
      return 'Submit feedback';
    }
    if (this.selectedOption === this.resolvedOptions[0]) {
      return 'Submit and cancel booking';
    }
    if (this.selectedOption === this.resolvedOptions[1]) {
      return 'Submit and continue booking';
    }
    return 'Submit feedback';
  }
}
